<template>
  <Toast />
  <div class="consumer-view">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <h1 class="title">Available Data Products</h1>
          <span class="catalog-badge">Consumer Catalog</span>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <section class="filters-section">
      <div class="filters-card">
        <div class="filters-row">
          <div class="search-field">
            <svg class="search-icon" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 21l-4.35-4.35m2.85-5.65a7 7 0 11-14 0 7 7 0 0114 0z"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
            <input
              v-model="globalFilters.search"
              type="text"
              placeholder="Search by name, description, or id"
              class="search-input"
            />
          </div>
          <div class="filters-header">
            <div class="filters-title"></div>
            <button class="ghost-toggle" @click="toggleAdvancedFilters">
              {{ showAdvancedFilters ? "Hide" : "Advanced Filters" }}
            </button>
          </div>
        </div>

        <transition name="fade">
          <div v-if="showAdvancedFilters" class="advanced-filters">
            <div class="advanced-grid">
              <div class="advanced-field">
                <label>Asset Type</label>
                <input
                  v-model="globalFilters.type"
                  type="text"
                  placeholder="e.g. HttpData, API, File"
                />
              </div>
              <div class="filter-chip">
                <label>Access</label>
                <select v-model="globalFilters.access" class="chip-select">
                  <option value="all">All</option>
                  <option value="granted">Granted</option>
                  <option value="restricted">Restricted</option>
                </select>
              </div>
              <div class="filter-chip">
                <label>Requirements</label>
                <select
                  v-model="globalFilters.requiresCredentials"
                  class="chip-select"
                >
                  <option value="all">All</option>
                  <option value="yes">Requires Credentials</option>
                  <option value="no">No Requirements</option>
                </select>
              </div>
              <div class="filter-chip">
                <label>Catalogs</label>
                <select v-model="globalFilters.catalog" class="chip-select">
                  <option value="all">All Catalogs</option>

                  <option
                    v-for="catalogName in uniqueCatalogNames"
                    :key="catalogName"
                    :value="catalogName"
                  >
                    {{ catalogName }}
                  </option>
                </select>
              </div>
            </div>
            <p class="advanced-hint">
              Advanced filters are placeholders for upcoming connector
              capabilities.
            </p>
          </div>
        </transition>
      </div>
    </section>

    <!-- Assets Section -->
    <section class="assets-section">
      <div v-if="filteredAssets.length === 0" class="empty-category">
        <div class="empty-icon">🔍</div>
        <h4>No matching assets</h4>
        <p>Try adjusting your filters to see results.</p>
      </div>

      <div v-else class="assets-grid">
        <div
          v-for="asset in paginatedAssets"
          :key="asset.id || asset?.['@id']"
          class="asset-card"
          :class="{
            expanded: expandedAssetId === (asset.id || asset?.['@id']),
          }"
        >
          <!-- Asset Header -->
          <div class="asset-header">
            <div class="asset-info">
              <div style="display: flex">
                <h4 class="asset-name">
                  {{ asset.name || asset.title || "Unnamed Asset" }}
                </h4>
                <div
                  style="
                    justify-content: flex-end;
                    display: flex;
                    flex: 1;
                    align-items: baseline;
                    gap: 6px;
                  "
                  v-if="asset?.pricingPlan"
                >
                  <span style="font-size: 22px; font-weight: 600">{{
                    asset?.pricingPlan?.price
                  }}</span>
                  <span style="font-size: 14px; font-weight: 600">{{
                    asset?.pricingPlan?.priceCurrency
                  }}</span>
                </div>
              </div>
              <div class="asset-meta">
                <span class="asset-type">{{
                  asset.type || asset.dataAddress?.type || "Data Asset"
                }}</span>
                <span
                  class="access-badge"
                  :class="
                    hasAcquiredCredentials(asset) ? 'granted' : 'restricted'
                  "
                >
                  {{
                    hasAcquiredCredentials(asset)
                      ? "Access Granted"
                      : "Access Restricted"
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Asset Description -->
          <p class="asset-description">
            {{
              asset.description ||
              "This asset requires Membership to view and negotiate."
            }}
          </p>

          <!-- Requirements Section -->

          <!-- Action Button -->
          <div class="asset-actions">
            <button
              class="request-btn"
              :class="{ disabled: !asset.can_contract }"
              :disabled="!asset.can_contract"
              @click="toggleAssetDetails(asset)"
              :title="
                !asset.can_contract ? 'Missing required credentials' : 'Details'
              "
            >
              Details
            </button>
            <button
              v-if="hasAcquiredCredentials(asset)"
              class="request-btn"
              :class="{ disabled: true }"
              :disabled="true"
            >
              Data Offer Acquired
            </button>
            <button
              v-else
              class="request-btn"
              @click="acquireAsset(asset)"
              :class="{ disabled: false }"
              :disabled="false"
            >
              Acquire Data Offer
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="filteredAssets.length > itemsPerPage" class="pagination-bar">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          Previous
        </button>
        <span class="page-meta">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          Next
        </button>
      </div>
    </section>

    <!-- Modal -->
    <Dialog
      v-model:visible="showAssetModal"
      modal
      header="Edit Profile"
      :style="{ width: '70%', background: '#f5f7fb' }"
    >
      <div class="modal__content">
        <div
          v-if="selectedAsset"
          class="asset-details asset-details--accordion"
        >
          <details class="detail-accordion" open>
            <summary>Basic Information</summary>
            <div class="detail-panel">
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Asset ID:</label>
                  <span>{{
                    selectedAsset.id || selectedAsset?.["@id"] || "N/A"
                  }}</span>
                </div>
                <div class="detail-item">
                  <label>Asset Name:</label>
                  <span>{{
                    selectedAsset.name || selectedAsset.title || "N/A"
                  }}</span>
                </div>
                <div class="detail-item">
                  <label>Type:</label>
                  <span>{{
                    selectedAsset.type ||
                    selectedAsset.dataAddress?.type ||
                    "Data Asset"
                  }}</span>
                </div>
                <div class="detail-item">
                  <label>Description:</label>
                  <span>{{
                    selectedAsset.description || "No description available"
                  }}</span>
                </div>
                <div class="detail-item" v-if="selectedAsset.AssetVersion">
                  <label>Asset Version:</label>
                  <span>{{ selectedAsset.AssetVersion }}</span>
                </div>
              </div>
            </div>
          </details>

          <details class="detail-accordion">
            <summary>Connector Information</summary>
            <div class="detail-panel">
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Catalog Name:</label>
                  <span>{{ selectedAsset.catalog_name || "N/A" }}</span>
                </div>
                <div class="detail-item">
                  <label>Endpoint:</label>
                  <span>{{ selectedAsset.endpoint || "N/A" }}</span>
                </div>
                <div class="detail-item">
                  <label>DID:</label>
                  <span>{{ selectedAsset.did || "N/A" }}</span>
                </div>
                <div class="detail-item" v-if="selectedAsset.format">
                  <label>Format:</label>
                  <span>{{ selectedAsset.format || "N/A" }}</span>
                </div>
              </div>
            </div>
          </details>

          <details class="detail-accordion">
            <summary>Policy Information</summary>
            <div class="detail-panel">
              <div
                v-if="
                  normalizePolicyArray(selectedAsset?.policy).filter(Boolean)
                    .length
                "
                class="policy-grid"
              >
                <article
                  v-for="(policy, index) in normalizePolicyArray(
                    selectedAsset?.policy
                  ).filter(Boolean)"
                  :key="getPolicyKey(policy, index)"
                  class="policy-modern-card"
                >
                  <div class="policy-modern-card__header">
                    <div class="policy-modern-card__icon">
                      <i class="pi pi-shield"></i>
                    </div>
                    <div class="policy-modern-card__title">
                      <h4>{{ getPolicyDisplayName(index) }}</h4>
                    </div>
                  </div>

                  <div v-if="policyHasRules(policy)" class="policy-rule-list">
                    <section
                      v-for="section in policyRuleSections"
                      :key="section.key"
                      v-show="
                        hasData(getPolicySectionData(policy, section.key))
                      "
                      class="policy-rule-section"
                    >
                      <div class="policy-rule-section__heading">
                        <span
                          :class="['policy-rule-dot', section.className]"
                        ></span>
                        {{ section.label }}
                      </div>

                      <div
                        v-for="(rule, ruleIndex) in normalizePolicyArray(
                          getPolicySectionData(policy, section.key)
                        )"
                        :key="`${section.key}-${ruleIndex}`"
                        class="policy-rule"
                      >
                        <div
                          v-for="(
                            constraint, constraintIndex
                          ) in getPolicyConstraints(rule)"
                          :key="constraintIndex"
                          class="policy-constraint"
                        >
                          <div class="policy-constraint-row">
                            <span>Left Operand</span>
                            <strong>{{
                              formatPolicyValue(
                                getPolicyFieldValue(
                                  constraint,
                                  "leftOperand",
                                  rule
                                )
                              )
                            }}</strong>
                          </div>
                          <div class="policy-constraint-row">
                            <span>Operator</span>
                            <strong>{{
                              formatPolicyValue(
                                getPolicyFieldValue(
                                  constraint,
                                  "operator",
                                  rule
                                )
                              )
                            }}</strong>
                          </div>
                          <div class="policy-constraint-row">
                            <span>Right Operand</span>
                            <strong>{{
                              formatPolicyValue(
                                getPolicyFieldValue(
                                  constraint,
                                  "rightOperand",
                                  rule
                                )
                              )
                            }}</strong>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>

                  <div v-else class="policy-empty-state">
                    No permission, prohibition, or obligation rules found.
                  </div>

                  <div class="policy-modern-card__actions">
                    <!-- <button
                      class="policy-action policy-action--primary"
                      @click="viewPolicy(policy, selectedAsset)"
                    >
                      View Policy
                    </button> -->
                  </div>
                </article>
              </div>

              <div v-else class="policy-empty-state">
                No policy information available.
              </div>
            </div>
          </details>

          <details class="detail-accordion" v-if="selectedAsset.pricingPlan">
            <summary>Pricing Plan</summary>
            <div class="detail-panel">
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Price:</label>
                  <span>{{ selectedAsset.pricingPlan.price || "N/A" }}</span>
                </div>
                <div class="detail-item">
                  <label>Type:</label>
                  <span>{{ selectedAsset.pricingPlan.unit || "N/A" }}</span>
                </div>
                <div class="detail-item">
                  <label>Currency:</label>
                  <span>{{
                    selectedAsset.pricingPlan.priceCurrency || "N/A"
                  }}</span>
                </div>
              </div>
            </div>
          </details>

          <details class="detail-accordion" v-if="selectedAsset.CreatorInfo">
            <summary>Creator Information</summary>
            <div class="detail-panel">
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Creator Name:</label>
                  <span>{{
                    selectedAsset.CreatorInfo.legalName || "N/A"
                  }}</span>
                </div>
                <div class="detail-item">
                  <label>Contact Name:</label>
                  <span>{{
                    selectedAsset.CreatorInfo.contactName || "N/A"
                  }}</span>
                </div>
                <div class="detail-item">
                  <label>Email:</label>
                  <span>{{ selectedAsset.CreatorInfo.email || "N/A" }}</span>
                </div>
                <div class="detail-item">
                  <label>URL:</label>
                  <span>{{ selectedAsset.CreatorInfo.url || "N/A" }}</span>
                </div>
                <div class="detail-item" v-if="selectedAsset.AssetVersion">
                  <label>ID:</label>
                  <span>{{ selectedAsset.CreatorInfo.uid }}</span>
                </div>
              </div>
            </div>
          </details>

          <details
            class="detail-accordion"
            v-if="selectedAsset.properties?.attributes"
          >
            <summary>Field Attributes</summary>
            <div class="detail-panel">
              <div class="attributes-container">
                <div v-if="Array.isArray(selectedAsset.properties.attributes)">
                  <div
                    v-for="(attribute, index) in selectedAsset.properties
                      .attributes"
                    :key="index"
                    class="attribute-card"
                  >
                    <div class="attribute-header">
                      <h4 class="field-name">
                        {{ attribute.fieldName || "Unnamed Field" }}
                      </h4>
                      <span class="data-type">{{
                        attribute.dataType || "Unknown Type"
                      }}</span>
                    </div>
                    <div class="attribute-details">
                      <div class="detail-row">
                        <span class="detail-label">Description:</span>
                        <span class="detail-value">{{
                          attribute.description || "No description"
                        }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Size:</span>
                        <span class="detail-value">{{
                          attribute.size || "N/A"
                        }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Value:</span>
                        <span class="detail-value">{{
                          attribute.value || "N/A"
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </details>
        </div>
        <div v-else class="loading-state">
          <p>Loading asset details...</p>
        </div>
      </div>
      <div class="modal__footer">
        <div class="modal-actions modal-actions--right">
          <button class="btn btn--secondary" @click="closeModal">Close</button>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import axios from "axios";
import Panel from "primevue/panel";
import Dialog from "primevue/dialog";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";

const toast = useToast();

const { appContext } = getCurrentInstance();
const API_BASE_URL = appContext.config.globalProperties.$apiBase;

const props = defineProps({
  credentials: {
    type: Array,
    default: () => [],
  },
  assets: {
    type: Array,
    required: true,
  },
  user: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["request-access"]);

// Refs
const expandedAssetId = ref(null);
const selectedParticipant = ref("did:web:provider-identityhub%3A7083:provider");
const catalogAssets = ref([]);
const localAssets = ref([]);
const subscribedEndpoints = ref([]);
const selectedAsset = ref(null);
const showAssetModal = ref(false);
const itemsPerPage = 8;
const currentPage = ref(1);
const showAdvancedFilters = ref(false);
const acquiredAssetIds = ref(new Set());

// Global filters
const globalFilters = ref({
  search: "",
  access: "all",
  requiresCredentials: "all",
  type: "",
  catalog: "all",
});

// Computed
const allAssets = computed(() => {
  return catalogAssets.value.length > 0
    ? catalogAssets.value
    : localAssets.value;
});

const uniqueCatalogNames = computed(() => {
  const names = allAssets.value
    .map((asset) => asset?.catalog_name)
    .filter((name) => normalizeText(name));

  return Array.from(new Set(names)).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
});

const normalizeText = (value) => (value || "").toString().trim().toLowerCase();

const normalizePolicyArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const getPolicyKey = (policy, index) => {
  return getPolicyProperty(policy, "id") || `policy-${index}`;
};

const getPolicyDisplayName = (index) => {
  return `Policy ${index + 1}`;
};

const policyHasRules = (policy) => {
  return policyRuleSections.some((section) =>
    hasData(getPolicySectionData(policy, section.key))
  );
};

const getPolicySectionData = (policy, sectionKey) => {
  return getPolicyProperty(policy, sectionKey);
};

const hasData = (value) => {
  if (!value) return false;

  if (Array.isArray(value)) {
    return (
      value.length > 0 && value.some((v) => Object.keys(v || {}).length > 0)
    );
  }

  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }

  return false;
};

const formatPolicyValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "object") {
    return (
      getPolicyProperty(value, "id") ||
      value["@value"] ||
      value.value ||
      JSON.stringify(value)
    );
  }
  return value;
};

const getPolicyConstraints = (rule) => {
  const constraints = normalizePolicyArray(
    getPolicyProperty(rule, "constraint")
  );
  return constraints.length ? constraints : [rule];
};

const getPolicyFieldValue = (source, fieldKey, fallbackSource) => {
  const value = getPolicyProperty(source, fieldKey);
  if (value !== undefined) return value;
  return getPolicyProperty(fallbackSource, fieldKey);
};

const assetMatchesSearch = (asset, query) => {
  if (!query) return true;
  const haystack = [
    asset?.name,
    asset?.title,
    asset?.description,
    asset?.id,
    asset?.["@id"],
    asset?.type,
  ]
    .filter(Boolean)
    .map((entry) => entry.toString().toLowerCase())
    .join(" ");
  return haystack.includes(query);
};

const getPolicyProperty = (source, aliasKey) => {
  if (!source || typeof source !== "object") return undefined;

  const aliases = policyFieldAliases[aliasKey] || [aliasKey];
  for (const alias of aliases) {
    if (source[alias] !== undefined && source[alias] !== null) {
      return source[alias];
    }
  }

  return undefined;
};

const policyFieldAliases = {
  id: ["@id", "id"],
  type: ["@type", "type"],
  permission: ["odrl:permission", "permission"],
  prohibition: ["odrl:prohibition", "prohibition"],
  obligation: ["odrl:obligation", "obligation"],
  hasPolicy: ["odrl:hasPolicy", "hasPolicy", "policy"],
  constraint: ["odrl:constraint", "constraint"],
  leftOperand: [
    "odrl:leftOperand",
    "leftOperand",
    "leftoperand",
    "left_operand",
  ],
  operator: ["odrl:operator", "operator"],
  rightOperand: [
    "odrl:rightOperand",
    "rightOperand",
    "rightoperand",
    "right_operand",
  ],
};

const policyRuleSections = [
  {
    key: "permission",
    label: "Permissions",
    className: "policy-rule-dot--permission",
  },
  {
    key: "prohibition",
    label: "Prohibitions",
    className: "policy-rule-dot--prohibition",
  },
  {
    key: "obligation",
    label: "Obligations",
    className: "policy-rule-dot--obligation",
  },
];

const filteredAssets = computed(() => {
  let assets = allAssets.value;
  const search = normalizeText(globalFilters.value.search);
  const typeFilter = normalizeText(globalFilters.value.type);

  return assets.filter((asset) => {
    // Search filter
    if (!assetMatchesSearch(asset, search)) return false;

    // Access filter
    if (
      globalFilters.value.access === "granted" &&
      !hasAcquiredCredentials(asset)
    )
      return false;
    if (
      globalFilters.value.access === "restricted" &&
      hasAcquiredCredentials(asset)
    )
      return false;

    // Catalog filter
    if (
      globalFilters.value.catalog !== "all" &&
      normalizeText(asset.catalog_name) !==
        normalizeText(globalFilters.value.catalog)
    )
      return false;

    // Requirements filter
    const hasReqs =
      Array.isArray(asset.required_credentials) &&
      asset.required_credentials.length > 0;
    if (globalFilters.value.requiresCredentials === "yes" && !hasReqs)
      return false;
    if (globalFilters.value.requiresCredentials === "no" && hasReqs)
      return false;

    // Type filter
    if (
      typeFilter &&
      !normalizeText(asset.type || asset.dataAddress?.type).includes(typeFilter)
    )
      return false;

    return true;
  });
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredAssets.value.length / itemsPerPage))
);

const paginatedAssets = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return filteredAssets.value.slice(start, start + itemsPerPage);
});

// Reset to first page when filters change
watch([globalFilters, () => filteredAssets.value.length], () => {
  currentPage.value = 1;
});

const hasCredential = (credentialType) => {
  if (!credentialType) return false;
  return props.credentials.some((cred) => {
    const credTypes = Array.isArray(cred.type) ? cred.type : [cred.type];
    return credTypes.some((type) => type === credentialType);
  });
};

const fetchAcquiredCredentials = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${props.user}`,
      {
        objectType: {
          name: "DS2DataOfferPackage",
        },
      },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 100,
          sortBy: "_id",
        },
      }
    );

    console.log("Repository search response for policies:", response.data);

    // Store acquired asset IDs in a Set for quick lookup
    response.data.forEach((record) => {
      if (record.contentData && record.contentData.id) {
        acquiredAssetIds.value.add(record.contentData.id);
      }
    });

    console.log("Acquired asset IDs:", Array.from(acquiredAssetIds.value));
  } catch (error) {
    console.error("Error fetching acquired assets:", error);
  }
};

// Simple function to check if asset is acquired (synchronous)
const hasAcquiredCredentials = (asset) => {
  return acquiredAssetIds.value.has(asset.id || asset["@id"]);
};

const acquireAsset = async (asset) => {
  console.log("user is: ", props.user);

  console.log("asset to acquire is: ", asset);

  const price = asset.Offer?.pricingPlans?.price;

  console.log("Price for the asset is: ", price);

  if (price > 0) {
    toast.add({
      severity: "error",
      summary: "Paid Asset",
      detail: "This paid asset can only be acquired from the data marketplace",
      life: 5000,
    });
    return;
  }

  const signer = await axios.post(
    "https://idm.ds2.icelab.cloud/idm-api/credentials/sign",
    {
      organizationDid: props.user,
      credentialType: "AcquisitionCredential",
      expirationDate: "2027-04-15T03:21:42.207",
      credentialSubject: {
        id: asset.Offer.participantId || asset.did,
        claims: {
          offerId: asset.Offer["odrl:hasPolicy"]["@id"],
          asset: asset.Offer.id,
          paid: false,
        },
      },
    },
    {
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
    }
  );

  await axios.post(
    `${API_BASE_URL}/repository/insert`,
    {
      ownerId: props.user,
      externalId: asset.did,
      objectType: {
        name: "AcquisitionCredential",
        level: 1,
      },
      name: `AcquisitionCredential-${asset.id}`,
      description: "",
      comments: "string",
      version: "string",
      tags: ["string"],
      metadata: {
        additionalProp1: {},
      },
      contentType: "json",
      contentData: signer.data,
    },
    {
      params: {
        "API-KEY": "C5CC3F19",
      },
      headers: {
        Accept: "application/json",
        "API-KEY": "C5CC3F19",
      },
    }
  );

  const dlmStoreCred = await axios.post(
    `${API_BASE_URL}/repository/find/prop/${props.user}`,
    {
      objectType: {
        name: "AcquisitionCredential",
        level: 1,
      },
      name: `AcquisitionCredential-${asset.id}`,
    },
    {
      params: {
        includeContent: "true",
        pageNumber: 1,
        pageSize: 100,
        sortBy: "_id",
      },
    }
  );

  console.log("DLM Store credential search response:", dlmStoreCred.data);

  if (!dlmStoreCred.data.length > 0) {
    toast.add({
      severity: "info",
      summary: "Acquisition Credential Failed",
      detail: "Failed to Acquire Credential. Please try again.",
      life: 3000,
    });
    return;
  }

  asset["Credential"] = dlmStoreCred.data[0].id;

  const dlmStore = await axios.post(
    `${API_BASE_URL}/repository/insert`,
    {
      ownerId: props.user,
      externalId: asset.name,
      objectType: {
        name: "DS2DataOfferPackage",
        level: 1,
      },
      name: asset.name,
      description: asset.description,
      comments: "string",
      version: "string",
      tags: ["string"],
      metadata: {
        source: "LocalCatalog",
      },
      contentType: "json",
      contentData: asset,
    },
    {
      params: {
        "API-KEY": "C5CC3F19",
      },
      headers: {
        Accept: "application/json",
        "API-KEY": "C5CC3F19",
      },
    }
  );

  console.log("DLM Store response:", dlmStore.data);

  if (dlmStore.data.success) {
    toast.add({
      severity: "success",
      summary: "Data Offer Acquired",
      life: 3000,
    });
  }
  await fetchAcquiredCredentials();
  await fetchNewAssets();
};

const viewPolicy = async (policy, asset) => {
  console.log("Viewing policy:", policy);
  console.log("Viewing asset:", asset);
  const policyId = getPolicyProperty(policy, "id");

  try {
    console.log(
      "policy id: ",
      `${API_BASE_URL}/dataOfferPolicy/${policy["@id"] || policy.id}`
    );

    const response = await axios.post(`${API_BASE_URL}/dataOfferPolicy`, {
      url: `${API_BASE_URL}/dataOfferPolicy/${policy["@id"] || policy.id}`,
    });
    window.open(response.data.url, "_blank");
  } catch (error) {
    console.log("Policy ID could not be decoded:", policyId, error);
  }

  selectedPolicyForPreview.value = policy;
  showPolicyDialog.value = true;
};

const isMissingCredential = (asset, credentialType) => {
  return !!(
    asset &&
    credentialType &&
    asset.missing_credentials &&
    asset.missing_credentials.includes(credentialType)
  );
};

const getCredentialConstraint = (asset, credentialType) => {
  return asset && credentialType && asset.credential_constraints
    ? asset.credential_constraints[credentialType]
    : null;
};

const formatCredentialType = (type) => {
  return !type
    ? "Unknown"
    : type
        .split(/[_-]/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

const toggleAdvancedFilters = () => {
  showAdvancedFilters.value = !showAdvancedFilters.value;
};

const fetchEndpoints = async (user) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/users/${user}/endpoints`);
    subscribedEndpoints.value = res.data;
  } catch (error) {
    console.error("Error fetching endpoints:", error);
  }
};

const fetchAssets = async (endpoint, did, catalogName) => {
  try {
    console.log(
      `Fetching assets for endpoint: ${endpoint}, did: ${did}, catalog: ${catalogName}`
    );
    const payload = {
      consumer_did: selectedParticipant.value,
      endpoint,
      did,
      catalog_name: catalogName,
    };
    const response = await axios.post(`${API_BASE_URL}/assets`, payload);
    console.log("Fetched assets from", endpoint, ":", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching assets:", error);
    return [];
  }
};

const fetchNewAssets = async () => {
  const assetsList = await Promise.all(
    subscribedEndpoints.value.map((ep) =>
      fetchAssets(ep.endpoint, ep.participant_did, ep.catalog_name)
    )
  );

  // Flatten the array of arrays into a single array
  catalogAssets.value = assetsList.flat().reverse();
  console.log("Updated catalog assets:", catalogAssets.value);
};

const toggleAssetDetails = (asset) => {
  console.log("Toggle asset details for:", asset);
  selectedAsset.value = asset;
  expandedAssetId.value = asset.id || asset["@id"];
  showAssetModal.value = true;
};

const closeModal = () => {
  showAssetModal.value = false;
  selectedAsset.value = null;
  expandedAssetId.value = null;
};

const normalizeTags = (tags) => {
  if (!tags) return [];
  return Array.isArray(tags) ? tags : [tags];
};

// Lifecycle
onMounted(async () => {
  await fetchEndpoints(props.user);
  await fetchNewAssets();
  await fetchAcquiredCredentials();
});
</script>

<style scoped>
.consumer-view {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #2d3748;
}

/* Filters */
.filters-card {
  padding: 1.5rem 1.5rem 1rem 1.5rem;
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.filters-title h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #0f172a;
}

.filters-subtitle {
  font-size: 0.9rem;
  color: #475569;
}

.ghost-toggle {
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: white;
  color: #0f172a;
  font-weight: 600;
  padding: 0.4rem 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ghost-toggle:hover {
  border-color: #0f766e;
  color: #0f766e;
}

.filters-row {
  display: grid;
  grid-template-columns: minmax(260px, 2fr) 1fr;
  gap: 1rem;
  align-items: center;
}

.search-field {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 0.75rem 1rem;
  box-shadow: inset 0 0 0 1px rgba(15, 118, 110, 0.08);
}

.search-icon {
  width: 20px;
  height: 20px;
  color: #0f766e;
}

.search-input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 0.95rem;
  color: #0f172a;
}

.quick-filters {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-chip {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: #64748b;
}

.chip-select {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 0.5rem 0.75rem;
  background: white;
  font-size: 0.85rem;
  color: #0f172a;
}

.advanced-filters {
  margin-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.25rem;
}

.advanced-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.advanced-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #475569;
}

.advanced-field input {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
}

.advanced-field.muted input {
  background: #f8fafc;
  color: #94a3b8;
}

.advanced-hint {
  margin: 0.75rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #009688 0%, #00695c 100%);
  border-radius: 16px;
  padding: 2.5rem;
  margin-bottom: 3rem;
  color: white;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 2rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.title {
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.catalog-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.endpoint-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.endpoint-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

.endpoint-url {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-family: "Fira Code", "Monaco", "Cascadia Code", monospace;
  font-size: 0.85rem;
  color: white;
  backdrop-filter: blur(10px);
}

/* Section Header */
.section-header {
  background-color: white;
  padding: 1.5rem;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.assets-count {
  font-size: 1rem;
  font-weight: 600;
  color: #718096;
  background: #f7fafc;
  padding: 0.5rem 1rem;
  border-radius: 20px;
}

/* Category Panel */
.category-panel {
  margin-bottom: 2rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1rem 1.5rem;
}

.category-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.category-count {
  font-size: 0.9rem;
  color: #718096;
  background: #edf2f7;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
}

.empty-category {
  text-align: center;
  padding: 2.5rem 1.5rem 2rem;
  color: #64748b;
}

.empty-category .empty-icon {
  font-size: 2.25rem;
  margin-bottom: 0.5rem;
}

.empty-category h4 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  color: #0f172a;
}

.empty-category p {
  margin: 0;
  font-size: 0.9rem;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem 1.5rem;
  gap: 1rem;
}

.page-btn {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 0.5rem 1rem;
  background: white;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  border-color: #009688;
  color: #009688;
}

.page-meta {
  font-size: 0.85rem;
  color: #475569;
}

/* Assets Grid */
.assets-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  padding: 1.5rem;
}

/* Asset Card */
.asset-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.asset-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.asset-card.expanded {
  border-color: #009688;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
}

/* Asset Header */
.asset-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.asset-info {
  flex: 1;
}

.asset-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.75rem 0;
  line-height: 1.4;
}

.asset-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.asset-type {
  background: #edf2f7;
  color: #4a5568;
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
}

.access-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

.access-badge.granted {
  background: #c6f6d5;
  color: #22543d;
}

.access-badge.restricted {
  background: #fed7d7;
  color: #742a2a;
}

.expand-btn {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.expand-btn:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
}

.expand-icon {
  width: 16px;
  height: 16px;
  fill: #4a5568;
  transition: transform 0.3s ease;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

/* Asset Description */
.asset-description {
  color: #718096;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
}

/* Requirements Section */
.requirements-section {
  margin: 1.5rem 0;
  padding: 1.25rem;
  background: #f7fafc;
  border-radius: 8px;
  border-left: 4px solid #009688;
}

.requirements-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 1rem 0;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.requirement-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.requirement-item.missing {
  background: #fff5f5;
  border-color: #fed7d7;
}

.requirement-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #c6f6d5;
  color: #22543d;
  font-size: 0.8rem;
  font-weight: 600;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.requirement-item.missing .requirement-icon {
  background: #fed7d7;
  color: #742a2a;
}

.requirement-details {
  flex: 1;
}

.credential-name {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.credential-constraint {
  font-size: 0.8rem;
  color: #718096;
  font-style: italic;
}

.no-requirements {
  text-align: center;
  color: #a0aec0;
  font-style: italic;
  padding: 1rem;
}

/* Asset Actions */
.asset-actions {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
}

.request-btn {
  background: #009688;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  margin-right: 10px;
}

.request-btn:hover:not(.disabled) {
  background: #5a67d8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.request-btn.disabled {
  background: #a0aec0;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  margin: 2rem 0;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  color: #718096;
  margin: 0;
  font-size: 1rem;
}

.p-panel {
  background-color: white;
}

/* Responsive Design */
@media (max-width: 768px) {
  .consumer-view {
    padding: 1rem;
  }

  .filters-row {
    grid-template-columns: 1fr;
  }

  .quick-filters {
    justify-content: flex-start;
  }

  .title {
    font-size: 2rem;
  }

  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .assets-grid {
    grid-template-columns: 1fr;
    padding: 1rem;
  }

  .asset-header {
    flex-direction: column;
    gap: 1rem;
  }

  .expand-btn {
    align-self: flex-end;
  }

  .panel-header {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .asset-meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .requirements-section {
    padding: 1rem;
  }

  .requirement-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .requirement-icon {
    margin-right: 0;
  }
}
/* ===== Modal Styles ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(205, 208, 219, 0.6);
  display: grid;
  place-items: center;
  z-index: 50;
  padding: 16px;
  backdrop-filter: blur(4px);
}

.modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 980px;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}

.modal--lg {
  max-width: 860px;
}

.modal--xl {
  max-width: 1040px;
}

.modal__header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal__title {
  display: flex;
  gap: 12px;
  align-items: center;
  color: #1a202c;
}

.modal__title h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
}

.close-btn {
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  color: #718096;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f7fafc;
  color: #2d3748;
}

.modal__content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal__footer {
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-size: 0.9rem;
}

.btn--primary {
  background: #009688;
  color: white;
}

.btn--primary:hover:not(.disabled) {
  background: #00796b;
  transform: translateY(-1px);
}

.btn--secondary {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.btn--secondary:hover {
  background: #edf2f7;
}

.btn.disabled {
  background: #a0aec0;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Asset Details Styles */
.asset-details {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.asset-details--accordion {
  margin-top: 0.25rem;
}

.detail-accordion {
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
}

.detail-accordion summary {
  list-style: none;
  cursor: pointer;
  padding: 1rem 1.15rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.detail-accordion summary::-webkit-details-marker {
  display: none;
}

.detail-accordion summary::after {
  content: "+";
  color: #115e59;
  font-weight: 900;
  font-size: 1.05rem;
}

.detail-accordion[open] summary::after {
  content: "-";
}

.detail-panel {
  padding: 1rem 1.15rem 1.15rem;
  border-top: 1px solid #e2e8f0;
}

.detail-section h3 {
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: row;
  gap: 4px;
}

.permissions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 15px;
}

.permission-card {
  border: 1px solid #ddd;
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.details-policy-header {
  text-align: center;
  background-color: #f5f7fb;
  font-weight: 600;
  margin-bottom: 10px;
}

.row {
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
}

.detail-item label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.9rem;
}

.detail-item span {
  color: #2d3748;
  line-height: 1.5;
  word-break: break-word;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #718096;
}

.modal-actions--right {
  justify-content: flex-end;
}

/* Badge Styles */
.badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background-color: #ecfdf5;
  color: #047857;
  font-size: 1.2rem;
}

.badge.pulse {
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
  }
  70% {
    box-shadow: 0 0 0 16px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: scale(0.95) translateY(-10px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal {
    margin: 16px;
    max-height: 90vh;
  }

  .modal__header {
    padding: 16px 20px;
  }

  .modal__content {
    padding: 20px;
  }

  .modal__footer {
    padding: 16px 20px;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .detail-grid,
  .permissions-grid {
    grid-template-columns: 1fr;
  }

  .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .modal__title {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .modal__title h1 {
    font-size: 1.25rem;
  }
}

.attributes-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.attribute-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.attribute-card:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.attribute-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.field-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.data-type {
  background: #3b82f6;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.attribute-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.detail-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  min-width: 100px;
  flex-shrink: 0;
}

.detail-value {
  color: #1e293b;
  font-size: 0.875rem;
  text-align: right;
  flex: 1;
  word-break: break-word;
}

/* Responsive */
@media (max-width: 768px) {
  .attribute-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .detail-row {
    flex-direction: column;
    gap: 0.25rem;
  }

  .detail-label {
    min-width: auto;
  }

  .detail-value {
    text-align: left;
  }
}
/* Add any additional styling needed */
.filters-section {
  margin-bottom: 2rem;
}

.filters-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.search-field {
  flex: 1;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #6b7280;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.ghost-toggle {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: background 0.2s;
}

.ghost-toggle:hover {
  background: #f3f4f6;
}

.advanced-filters {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.advanced-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.advanced-field label,
.filter-chip label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.advanced-field input,
.chip-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
}

.advanced-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 1rem;
  font-style: italic;
}

.assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
}

.page-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-meta {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal--lg {
  width: 90%;
}

.modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal__title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal__content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.modal__footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.policy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 16px;
}

.policy-modern-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  padding: 18px;
}

.policy-modern-card__header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
}

.policy-modern-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: #00796b;
  background: #e6f6f3;
}

.policy-modern-card__title {
  min-width: 0;
}

.policy-modern-card__eyebrow {
  display: block;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.policy-modern-card__title h4 {
  margin: 2px 0 0;
  color: #0f172a;
  font-size: 0.98rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.policy-modern-card__count {
  border: 1px solid #cde8e3;
  border-radius: 999px;
  background: #f0fbf8;
  color: #00796b;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 5px 10px;
  white-space: nowrap;
}

.policy-constraint-row span,
.policy-preview-dialog__header span {
  display: block;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.policy-constraint-row strong {
  display: block;
  margin-top: 4px;
  color: #1e293b;
  font-size: 0.88rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.policy-rule-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.policy-rule-section {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fbfdff;
  padding: 12px;
}

.policy-rule-section__heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
}

.policy-rule-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #009688;
}

.policy-rule-dot--prohibition {
  background: #dc2626;
}

.policy-rule-dot--obligation {
  background: #f59e0b;
}

.policy-rule,
.policy-constraint {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.policy-constraint + .policy-constraint {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #cbd5e1;
}

.policy-constraint-row {
  display: grid;
  grid-template-columns: minmax(90px, 0.42fr) minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}

.policy-constraint-row strong {
  margin-top: 0;
  font-weight: 600;
}

.policy-empty-state {
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.9rem;
  padding: 14px;
  text-align: center;
}

.policy-modern-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
  padding-top: 2px;
}

.policy-action {
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 800;
  padding: 9px 14px;
  transition: all 0.2s ease;
}

.policy-action--ghost {
  background: #ffffff;
  border-color: #cbd5e1;
  color: #334155;
}

.policy-action--ghost:hover {
  background: #f8fafc;
}

.policy-action--primary {
  background: #009688;
  color: #ffffff;
}

.policy-action--primary:hover {
  background: #00796b;
}

.policy-preview-dialog {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.policy-preview-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 16px;
}

.policy-preview-dialog__header h3 {
  margin: 3px 0 0;
  color: #0f172a;
  font-size: 1rem;
  overflow-wrap: anywhere;
}

.policy-preview-dialog pre {
  max-height: 56vh;
  overflow: auto;
  margin: 0;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 0.82rem;
  line-height: 1.55;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-word;
}

.permissions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 15px;
}

.permission-card {
  border: 1px solid #ddd;
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.details-policy-header {
  text-align: center;
  background-color: #f5f7fb;
  font-weight: 600;
  margin-bottom: 10px;
}

.row {
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
}
</style>
